/**
 * PATH: packages/pricelist/src/createPricelistClient.ts
 * FILE_ID: PKG-PRICELIST-CLIENT-001
 * PURPOSE: Adapt any axios-like instance into the PricelistClient contract so
 *          the workbook runs unchanged on the master SPA, the Quote Hub and
 *          the SAM/Newmark tenant axios singletons.
 * WHY: Each host already owns auth + org-scoping on its axios instance; we
 *      only layer the quotebuilder pricelist routes on top.
 * RELATED: types.ts (PricelistClient), quotebuilder/urls.py (routes)
 */
import type {
  BulkUpsertResult,
  ColumnDef,
  PriceBookSummary,
  PricelistClient,
  PricelistItem,
  PricelistSection,
  UpgradeResult,
  UpsertRow,
} from './types'

/** The slice of axios the client uses (so we don't import axios here). */
export interface AxiosLike {
  get(url: string, config?: { params?: Record<string, unknown> }): Promise<{ data: unknown }>
  post(url: string, body?: unknown): Promise<{ data: unknown }>
  patch(url: string, body?: unknown): Promise<{ data: unknown }>
  delete(url: string): Promise<{ data: unknown }>
}

/** DRF returns either a bare array or {results: [...]}. Normalise to array. */
function asArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[]
  const results = (data as { results?: unknown })?.results
  return Array.isArray(results) ? (results as T[]) : []
}

export interface PricelistClientOptions {
  /**
   * @deprecated Kept for source compatibility. IQ authorization now comes only
   * from the authenticated user's API-backed preference; query elevation is
   * intentionally ignored.
   */
  includeHarvested?: boolean
}

export function createPricelistClient(
  http: AxiosLike,
  basePath = '/api/quotebuilder',
  _options: PricelistClientOptions = {},
): PricelistClient {
  const base = basePath.replace(/\/$/, '')
  const pb = (id: string) => `${base}/pricebooks/${id}`
  const sec = (id: string, sid: string) => `${pb(id)}/sections/${sid}`

  return {
    async listPricebooks() {
      const { data } = await http.get(`${base}/pricebooks/`)
      return asArray<PriceBookSummary>(data)
    },
    async createPricebook(name: string) {
      const { data } = await http.post(`${base}/pricebooks/`, { name })
      return data as PriceBookSummary
    },
    async upgradePricebook(pricebookId: string) {
      const { data } = await http.post(`${pb(pricebookId)}/upgrade/`, {})
      return data as UpgradeResult
    },
    async listSections(pricebookId) {
      const { data } = await http.get(`${pb(pricebookId)}/sections/`, { params: {} })
      return asArray<PricelistSection>(data)
    },
    async listItems(pricebookId, sectionId) {
      const { data } = await http.get(`${pb(pricebookId)}/items/`, {
        params: { page_size: 1000, ...(sectionId ? { section: sectionId } : {}) },
      })
      return asArray<PricelistItem>(data)
    },
    async createSection(pricebookId, body) {
      const { data } = await http.post(`${pb(pricebookId)}/sections/`, body)
      return data as PricelistSection
    },
    async updateSection(pricebookId, sectionId, body) {
      const { data } = await http.patch(`${sec(pricebookId, sectionId)}/`, body)
      return data as PricelistSection
    },
    async deleteSection(pricebookId, sectionId) {
      await http.delete(`${sec(pricebookId, sectionId)}/`)
    },
    async addColumn(pricebookId, sectionId, column: ColumnDef, index) {
      const { data } = await http.post(`${sec(pricebookId, sectionId)}/columns/add/`, {
        column,
        ...(index != null ? { index } : {}),
      })
      return data as PricelistSection
    },
    async renameColumn(pricebookId, sectionId, oldKey, newKey, label) {
      const { data } = await http.post(`${sec(pricebookId, sectionId)}/columns/rename/`, {
        old_key: oldKey,
        new_key: newKey,
        ...(label ? { label } : {}),
      })
      return data as PricelistSection
    },
    async archiveColumn(pricebookId, sectionId, key) {
      // confirm:true — the server refuses column archives without an explicit
      // confirmation (the UI shows its own confirm step before calling this).
      const { data } = await http.post(`${sec(pricebookId, sectionId)}/columns/archive/`, {
        key,
        confirm: true,
      })
      return data as PricelistSection
    },
    async applyColumnView(pricebookId, body) {
      const { data } = await http.post(
        `${pb(pricebookId)}/sections/columns/apply_view/`,
        body,
      )
      return data as { sections_updated: number }
    },
    async applyDiscount(pricebookId, sectionId, body) {
      const { data } = await http.post(`${sec(pricebookId, sectionId)}/apply_discount/`, body)
      return data as { items_updated: number }
    },
    async bulkUpsert(pricebookId, sectionId, rows: UpsertRow[], deleteIds: string[]) {
      const { data } = await http.post(`${sec(pricebookId, sectionId)}/items/bulk_upsert/`, {
        items: rows,
        delete_ids: deleteIds,
      })
      return data as BulkUpsertResult
    },
    async harvest(body) {
      const { data } = await http.post(`${base}/pricing/harvest-quotes/`, body)
      return data as Record<string, unknown>
    },
  }
}
